import { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  fetchSearchPodcasts,
  fetchSearchPodcastsByCategory,
  updateCategoriesWithPodcastsDataAfterFetch
} from '../redux/actions/app-actions';
import { setFeedUrl } from '../redux/actions/player-actions';
import { firebase } from '../firebase';
import useFetchCategories from '../hooks/useFetchcategories';
import withAuthorization from '../session/with_authorization';
import Navigation from '../components/header/Navigation';
import CategoryHeader from '../components/podcasts/CategoryHeader';
import CategoryHeaderNew from '../components/podcasts/CategoryHeaderNew';
import Podcast from '../components/podcasts/Podcast';
import SearchBar from '../components/search/SearchBar';
import CategoryPodcastSkeletonLoader from '../components/ui/skeleton/CategoryPodcastSkeletonLoader';
import NoResults from '../features/discover/components/NoResults';
import PodcastWithDocumentsWrapper from '../features/discover/components/PodcastWithDocumentsWrapper';
import RefreshButton from '../components/ui/button/RefreshButton';
import {
  isUserStatusFree,
  isUserStatusSubscribed
} from '../utils/CheckUserStatus';
import {
  isSubscriptionTypeElite,
  isSubscriptionTypePro
} from '../utils/CheckSubscriptionType';
import AdvertisingChannel from '../components/ui/container/AdvertisingChannel';

const Mode = {
  INIT: 'init',
  SEARCH: 'search',
  PODCASTS: 'podcasts',
  DOCUMENTS: 'documents'
};

const Container = styled.div`
  margin: 30px 100px;
`;

const DiscoverPage = (props) => {
  const abortControllerRef = useRef(null);

  const { loading, data, setData, fetchData } = useFetchCategories(
    props.categoriesWithPodcasts,
    props.updateCategories
  );
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [mode, setMode] = useState(Mode.INIT); // page mode on of <init | search | podcasts | documents>
  const [prevMode, setPrevMode] = useState(Mode.INIT);
  const [searchValue, setSearchValue] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [category, setCategory] = useState(null);
  const [podcast, setPodcast] = useState(null);

  const fetchSearchData = async () => {
    const controller = new AbortController();
    const signal = controller.signal;
    abortControllerRef.current = controller;

    try {
      if (firebase.auth.currentUser) {
        const token = await firebase.auth.currentUser.getIdToken();
        setIsLoadingResults(true);
        setMode(Mode.SEARCH);
        setDisplayValue(searchValue);

        const res = await fetchSearchPodcasts(token, searchValue, { signal });

        if (!signal.aborted) {
          const { data } = res;
          setData(data);
          setIsLoadingResults(false);
        }
      }
    } catch (error) {
      if (!signal.aborted) {
        setData([]);
      }
    }

    return controller;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    if (value === '') {
      goToInitMode();
      return;
    }
    setSearchValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      searchValue === '' ? goToInitMode() : fetchSearchData();
    }
  };

  const handleSearchClick = () => {
    searchValue === '' ? goToInitMode() : fetchSearchData();
  };

  const handleInputClear = () => {
    if (mode === Mode.INIT) {
      setSearchValue('');
    } else {
      goToInitMode();
    }
  };

  const showPodcasts = async (category) => {
    const controller = new AbortController();
    const signal = controller.signal;
    abortControllerRef.current = controller;

    try {
      if (firebase.auth.currentUser) {
        const token = await firebase.auth.currentUser.getIdToken();
        setIsLoadingResults(true);
        setMode(Mode.PODCASTS);
        setSearchValue('');
        setDisplayValue(category.category);

        const res = await fetchSearchPodcastsByCategory(
          token,
          category.categoryId,
          { signal }
        );

        if (!signal.aborted) {
          const { data } = res;
          setData(data);
          setIsLoadingResults(false);
          window.scrollTo(0, 0);
        }
      }
    } catch (error) {
      if (!signal.aborted) {
        setIsLoadingResults(false);
      }
    }

    return controller;
  };

  const showPodcastDocuments = (podcast, category) => {
    const prevMode = mode;
    props.updateFeedUrl(podcast.feedUrl);
    setPrevMode(prevMode);
    setCategory(category);
    setPodcast(podcast);
    setMode(Mode.DOCUMENTS);
  };

  const goToInitMode = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMode(Mode.INIT);
    setData(props.categoriesWithPodcasts);
    setSearchValue('');
    setIsLoadingResults(false);
  };

  const goBack = () => {
    if (prevMode === Mode.INIT) {
      goToInitMode();
      return;
    }

    if (prevMode === Mode.SEARCH) {
      setMode(Mode.SEARCH);
      return;
    }

    if (prevMode === Mode.PODCASTS) {
      setMode(Mode.PODCASTS);
      return;
    }
  };

  const refresh = () => {
    fetchData(true);
  };

  return (
    <>
      <div>
        <Navigation />
      </div>
      {loading && (
        <Container>
          <CategoryPodcastSkeletonLoader
            numOfCategories={4}
            backgroundColor="#262E35"
          />
        </Container>
      )}
      {!loading && mode !== Mode.DOCUMENTS && (
        <Container>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '40px'
            }}
          >
            {mode !== Mode.DOCUMENTS && (
              <SearchBar
                value={searchValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSearchClick={handleSearchClick}
                onClear={handleInputClear}
                disabled={isLoadingResults}
              />
            )}
            {mode === Mode.INIT && <RefreshButton onClick={refresh} />}
          </div>
          {isLoadingResults && (
            <CategoryPodcastSkeletonLoader
              {...{ numOfCategories: mode === Mode.SEARCH ? 4 : 1 }}
              {...{ numOfPodcasts: mode === Mode.SEARCH ? 10 : 20 }}
              backgroundColor="#262E35"
            />
          )}
          {!isLoadingResults && mode === Mode.SEARCH && data?.length === 0 && (
            <NoResults value={displayValue} />
          )}
          {!isLoadingResults &&
            (mode === Mode.INIT || mode === Mode.SEARCH) &&
            data?.length > 0 &&
            (data || []).map((podcastsList) => (
              <div key={podcastsList.category}>
                <CategoryHeader
                  category={podcastsList}
                  onClick={showPodcasts}
                />
                <>
                  {podcastsList.categoryImageUrl && (
                    <AdvertisingChannel
                      src={podcastsList.categoryImageUrl}
                      width="240px"
                      height="120px"
                    />
                  )}
                  {(podcastsList.podcasts || []).map((s) => (
                    <Podcast
                      key={s.podcastId}
                      podcast={s}
                      onClick={() => showPodcastDocuments(s, podcastsList)}
                    />
                  ))}
                </>
              </div>
            ))}
          {!isLoadingResults && mode === Mode.PODCASTS && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}
              >
                <Button
                  bsClass="custom-button-discover"
                  onClick={goToInitMode}
                  style={{
                    marginTop: '5px',
                    marginBottom: '5px',
                    marginRight: '20px',
                    width: '70px'
                  }}
                >
                  <i
                    className="fa fa-chevron-left"
                    style={{ marginRight: '5px' }}
                  ></i>
                  Back
                </Button>
                <CategoryHeaderNew category={displayValue} />
              </div>
              {(data || []).map((s) => (
                <Podcast
                  key={s.podcastId}
                  podcast={s}
                  onClick={() => showPodcastDocuments(s)}
                />
              ))}
            </>
          )}
        </Container>
      )}
      {mode === Mode.DOCUMENTS && (
        <PodcastWithDocumentsWrapper
          category={category}
          podcast={podcast}
          goBack={goBack}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  categoriesWithPodcasts: state.app.categoriesWithPodcasts
});

const mapDispatchToProps = (dispatch) => ({
  updateCategories: (data) =>
    dispatch(updateCategoriesWithPodcastsDataAfterFetch(data)),
  updateFeedUrl: (value) => dispatch(setFeedUrl(value))
});

const authCondition = (authUser) =>
  !!authUser &&
  (isUserStatusSubscribed(authUser.userStatus) ||
    isUserStatusFree(authUser.userStatus)) &&
  (authUser.subscriptionType === undefined ||
    isSubscriptionTypePro(authUser.subscriptionType) ||
    isSubscriptionTypeElite(authUser.subscriptionType));

export default withAuthorization(authCondition)(
  connect(mapStateToProps, mapDispatchToProps)(DiscoverPage)
);
